import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ImageUpload } from "./ImageUpload";
import { Trash2 } from "lucide-react";

interface Item {
  id: string; name: string; location: string | null; rating: number;
  message: string; photo_url: string | null; sort_order: number; visible: boolean;
}

const EMPTY = { name: "", location: "", rating: 5, message: "", photo_url: "" };

export function TestimonialsTab() {
  const [items, setItems] = useState<Item[]>([]);
  const [draft, setDraft] = useState(EMPTY);

  async function load() {
    const { data } = await supabase.from("testimonials").select("*").order("sort_order");
    setItems((data as Item[]) || []);
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!draft.name || !draft.message) return toast.error("Name and message required");
    const { error } = await supabase.from("testimonials").insert({ ...draft, sort_order: items.length });
    if (error) return toast.error(error.message);
    setDraft(EMPTY); load(); toast.success("Added");
  }

  async function update(id: string, patch: Partial<Item>) {
    const { error } = await supabase.from("testimonials").update(patch).eq("id", id);
    if (error) toast.error(error.message); else load();
  }

  async function remove(id: string) {
    const { error } = await supabase.from("testimonials").delete().eq("id", id);
    if (error) toast.error(error.message); else { load(); toast.success("Deleted"); }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl">Testimonials</h2>
        <p className="text-sm text-muted-foreground">Customer reviews shown on the website.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="font-semibold">Add new testimonial</div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="e.g. Mansa" className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label>Rating (1-5)</Label>
          <Input type="number" min={1} max={5} value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })} className="mt-1.5 w-24" />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea rows={3} value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} className="mt-1.5" />
        </div>
        <ImageUpload value={draft.photo_url} onChange={(url) => setDraft({ ...draft, photo_url: url })} folder="testimonials" label="Customer photo (optional)" />
        <Button onClick={add} size="sm">Add testimonial</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="flex items-start gap-3">
              {it.photo_url && <img src={it.photo_url} alt={it.name} className="h-12 w-12 rounded-full object-cover" />}
              <div className="flex-1">
                <div className="font-semibold">{it.name}</div>
                <div className="text-xs text-muted-foreground">{it.location} · {"★".repeat(it.rating)}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(it.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <p className="text-sm">{it.message}</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={it.visible} onChange={(e) => update(it.id, { visible: e.target.checked })} />
              Visible
            </label>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No testimonials yet.</p>}
      </div>
    </div>
  );
}
